import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Search, MapPin, Locate, Coffee, Building2, BookOpen, Beer, BriefcaseBusiness, List, X, Navigation, Plus, MoreHorizontal, Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import Header from "../components/Header";
import VenueMap from "../components/VenueMap";
import VenueCard from "../components/VenueCard";
import NearbyPanel from "../components/NearbyPanel";
import AdBanner from "../components/AdBanner";
import SEO from "../components/SEO";
import WelcomeModal from "../components/WelcomeModal";
import { toast } from "sonner";
import { trackPageView, incrementPageCount } from "../utils/analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const venueTypes = [
  { type: "cafe", icon: Coffee, label: "Cafes" },
  { type: "pub", icon: Beer, label: "Pubs" },
  { type: "hotel", icon: Building2, label: "Hotels" },
  { type: "library", icon: BookOpen, label: "Libraries" },
  { type: "hub", icon: BriefcaseBusiness, label: "Hubs" },
  { type: "other", icon: MoreHorizontal, label: "Other" },
];

// Currency and language settings by country
const LOCALE_SETTINGS = {
  GB: { currency: "GBP", symbol: "£", language: "en-GB" },
  US: { currency: "USD", symbol: "$", language: "en-US" },
  EU: { currency: "EUR", symbol: "€", language: "en-EU" },
  default: { currency: "GBP", symbol: "£", language: "en-GB" }
};

export default function HomePage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [venues, setVenues] = useState([]);
  const [nearbyVenues, setNearbyVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userLocation, setUserLocation] = useState(null);
  const [mapCenter, setMapCenter] = useState([51.5074, -0.1278]);
  const [selectedVenue, setSelectedVenue] = useState(null);
  const [showVenueList, setShowVenueList] = useState(false);
  const [activeFilters, setActiveFilters] = useState([]); // Changed to array for multi-select
  const [gettingLocation, setGettingLocation] = useState(false);
  const [showNearbyPanel, setShowNearbyPanel] = useState(false);
  const [localeSettings, setLocaleSettings] = useState(LOCALE_SETTINGS.default);
  const [hasAutoLocated, setHasAutoLocated] = useState(false);
  const [lastVenueCount, setLastVenueCount] = useState(0);
  
  // Refs to track initialization
  const initializedRef = useRef(false);

  // Fetch venues function wrapped in useCallback
  const fetchVenues = useCallback(async (type = null) => {
    try {
      const params = type ? { venue_type: type } : {};
      const response = await axios.get(`${API}/venues`, { params });
      const venueData = response.data || [];
      setVenues(venueData);
      setLastVenueCount(venueData.length);
      return venueData;
    } catch (error) {
      console.error("Error fetching venues:", error);
      toast.error("Failed to load venues. Please refresh the page.");
      return [];
    }
  }, []);

  // Fetch nearby venues
  const fetchNearbyVenues = useCallback(async (lat, lng) => {
    try {
      const response = await axios.get(`${API}/venues/nearby`, {
        params: { lat, lng, radius_km: 50, limit: 20 }
      });
      setNearbyVenues(response.data);
      return response.data;
    } catch (error) {
      console.error("Error fetching nearby venues:", error);
      return [];
    }
  }, []);

  // Initialize data on mount
  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    
    const init = async () => {
      trackPageView("home");
      incrementPageCount();
      await fetchVenues();
      setLoading(false);
    };
    init();
  }, [fetchVenues]); // trackPageView and incrementPageCount are stable module-level functions

  // Poll for new venues every 30 seconds
  useEffect(() => {
    const pollInterval = setInterval(async () => {
      try {
        const response = await axios.get(`${API}/venues`);
        const newVenues = response.data;
        
        // Check if we have new venues
        if (newVenues.length > lastVenueCount && lastVenueCount > 0) {
          const newCount = newVenues.length - lastVenueCount;
          toast.success(`${newCount} new workspace${newCount > 1 ? 's' : ''} just added!`, {
            action: {
              label: 'View',
              onClick: () => setShowVenueList(true)
            }
          });
        }
        
        setVenues(newVenues);
        setLastVenueCount(newVenues.length);
      } catch (error) {
        console.error("Error polling venues:", error);
      }
    }, 30000);

    return () => clearInterval(pollInterval);
  }, [lastVenueCount]);

  // Auto-locate user on page load
  const autoLocateUser = useCallback(() => {
    if ("geolocation" in navigator) {
      setGettingLocation(true);
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          setUserLocation({ lat: latitude, lng: longitude });
          setMapCenter([latitude, longitude]);
          
          // Detect country for locale settings
          try {
            const geoResponse = await axios.get(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
            );
            const countryCode = geoResponse.data?.address?.country_code?.toUpperCase();
            if (countryCode && LOCALE_SETTINGS[countryCode]) {
              setLocaleSettings(LOCALE_SETTINGS[countryCode]);
            }
          } catch (localeError) {
            console.debug("Locale detection skipped:", localeError.message);
          }
          
          await fetchNearbyVenues(latitude, longitude);
          setGettingLocation(false);
          setHasAutoLocated(true);
        },
        () => {
          setGettingLocation(false);
          setHasAutoLocated(true);
        },
        { enableHighAccuracy: false, timeout: 5000, maximumAge: 300000 }
      );
    }
  }, [fetchNearbyVenues]);

  useEffect(() => {
    if (!hasAutoLocated && !loading) {
      autoLocateUser();
    }
  }, [loading, hasAutoLocated, autoLocateUser]);

  const getUserLocation = useCallback((showPanel = false) => {
    setGettingLocation(true);
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          setUserLocation({ lat: latitude, lng: longitude });
          setMapCenter([latitude, longitude]);
          const nearby = await fetchNearbyVenues(latitude, longitude);
          if (nearby.length > 0) {
            if (showPanel) {
              setShowNearbyPanel(true);
              setShowVenueList(false);
            } else {
              setShowVenueList(true);
            }
            toast.success(`Found ${nearby.length} workspaces near you!`);
          } else {
            toast.info("No workspaces found within 50km.");
          }
          setGettingLocation(false);
        },
        (error) => {
          // Geolocation errors are expected (user denied, unavailable, timeout)
          // Use console.warn instead of console.error to avoid error tracking
          console.warn("Geolocation unavailable:", error.message || error.code);
          toast.error("Unable to get your location. Please enable location services or search manually.");
          setGettingLocation(false);
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    } else {
      toast.error("Geolocation not supported");
      setGettingLocation(false);
    }
  }, [fetchNearbyVenues]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/browse?search=${encodeURIComponent(searchQuery)}`);
    }
  };

  const handleTypeFilter = (type) => {
    setActiveFilters(prev => {
      if (prev.includes(type)) {
        // Remove the type if already selected
        return prev.filter(t => t !== type);
      } else {
        // Add the type to selection
        return [...prev, type];
      }
    });
    setShowVenueList(true);
  };

  // Venues to display in the LIST (filtered by location when nearby)
  const displayVenues = nearbyVenues.length > 0 ? nearbyVenues : venues;
  
  // Filter venues based on selected categories
  // If no filters selected, show all venues
  const filteredVenues = activeFilters.length > 0 
    ? venues.filter(v => activeFilters.includes(v.venue_type))
    : venues;
  
  // Map shows filtered venues based on category selection
  const mapVenues = filteredVenues;
  
  // Filter display venues too
  const filteredDisplayVenues = activeFilters.length > 0
    ? displayVenues.filter(v => activeFilters.includes(v.venue_type))
    : displayVenues;
  
  // Show hero on initial load (no search performed yet)
  const showHero = !searchQuery && activeFilters.length === 0 && !userLocation;

  return (
    <div className="h-screen flex flex-col bg-surface overflow-hidden">
      <SEO 
        title="Strider | Find Verified Workspaces in UK"
        description="Don't gamble on bad Wi-Fi. Discover the UK's best cafes, pubs, and hotels for remote working. Find verified workspaces with measured WiFi speeds and power outlets."
        keywords="remote work UK, laptop friendly cafes, work from cafe London, coworking space near me, digital nomad spots UK, best WiFi cafes, verified workspace, strider"
        canonical="/"
      />
      <Header />
      
      {/* Welcome Modal for First-Time Visitors */}
      <WelcomeModal />
      
      {/* Main Content - starts after header (h-14 = 56px) */}
      <div className="flex-1 relative mt-14">
        {/* Map Container - Full height, starts below header */}
        <div className="absolute inset-0">
          <VenueMap 
            venues={mapVenues} 
            center={mapCenter}
            zoom={userLocation ? 12 : 6}
            selectedVenue={selectedVenue}
            userLocation={userLocation}
          />
        </div>
        
        {/* Hero Overlay - Big Search Bar */}
        {showHero && (
          <div className="absolute inset-0 z-[850] flex items-center justify-center bg-gradient-to-b from-black/60 via-black/40 to-transparent pointer-events-none">
            {/* Strider Logo Watermark */}
            <div 
              className="absolute inset-0 opacity-[0.04] pointer-events-none"
              style={{
                backgroundImage: 'url(/strider-logo-bg.png)',
                backgroundSize: '500px',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                filter: 'brightness(3)'
              }}
            />
            <div className="text-center px-4 max-w-3xl pointer-events-auto relative">
              <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 drop-shadow-lg">
                Don't gamble on bad Wi-Fi.
              </h1>
              <p className="text-lg sm:text-xl text-white/90 mb-8 drop-shadow">
                Discover the UK's best cafes, pubs, and hotels for remote working.
              </p>
              
              {/* Big Search Bar */}
              <form onSubmit={handleSearch} className="relative max-w-2xl mx-auto mb-6">
                <Search className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400 w-6 h-6" />
                <input
                  type="text"
                  placeholder="Search by town, city, or postcode..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-14 pr-32 py-5 text-lg rounded-2xl border-0 shadow-2xl focus:ring-4 focus:ring-brand/30 focus:outline-none"
                  data-testid="hero-search-input"
                />
                <button 
                  type="submit" 
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-brand text-white px-6 py-3 rounded-xl text-base font-semibold hover:bg-brand-dark transition-colors shadow-lg"
                  data-testid="hero-search-button"
                >
                  Search
                </button>
              </form>
              
              {/* Quick Actions */}
              <div className="flex flex-wrap items-center justify-center gap-3">
                <button
                  onClick={getUserLocation}
                  disabled={gettingLocation}
                  className="flex items-center gap-2 px-4 py-2.5 bg-white/90 backdrop-blur rounded-xl text-gray-800 font-medium hover:bg-white transition-colors shadow-lg"
                  data-testid="hero-find-nearest"
                >
                  {gettingLocation ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Locate className="w-4 h-4" />
                  )}
                  Find Near Me
                </button>
                <Link
                  to="/browse"
                  className="flex items-center gap-2 px-4 py-2.5 bg-white/20 backdrop-blur rounded-xl text-white font-medium hover:bg-white/30 transition-colors"
                >
                  <List className="w-4 h-4" />
                  Browse All
                </Link>
              </div>
              
              {/* Category Filter Buttons - Multi-select */}
              <div className="flex flex-wrap gap-2 justify-center mt-6 max-w-xl mx-auto">
                {venueTypes.map((item) => {
                  const isActive = activeFilters.includes(item.type);
                  return (
                    <button
                      key={item.type}
                      onClick={() => handleTypeFilter(item.type)}
                      className={`flex items-center gap-1.5 px-3 py-2 rounded-lg whitespace-nowrap text-sm font-medium transition-all backdrop-blur ${
                        isActive
                          ? "bg-brand text-white shadow-lg"
                          : "bg-white/90 text-gray-700 hover:bg-white shadow-md"
                      }`}
                      data-testid={`filter-${item.type}`}
                    >
                      <item.icon className="w-4 h-4" />
                      <span>{item.label}</span>
                    </button>
                  );
                })}
                {activeFilters.length > 0 && (
                  <button
                    onClick={() => setActiveFilters([])}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg whitespace-nowrap text-sm font-medium transition-all bg-gray-200/90 text-gray-600 hover:bg-gray-300/90 backdrop-blur"
                    data-testid="clear-type-filters"
                  >
                    <X className="w-4 h-4" />
                    <span>Clear</span>
                  </button>
                )}
              </div>
              
              {/* Stats */}
              <div className="mt-8 flex items-center justify-center gap-6 text-white/80 text-sm">
                <span>{venues.length}+ Verified Workspaces</span>
                <span className="w-1 h-1 bg-white/50 rounded-full" />
                <span>Trusted by Remote Workers</span>
              </div>
            </div>
          </div>
        )}

        {/* Search Bar & Filters Container - shows after search */}
        {!showHero && (
          <div className="absolute top-4 md:top-2 left-0 right-0 z-[900] px-4 space-y-2 pt-1">
          {/* Search Bar */}
          <form onSubmit={handleSearch} className="relative max-w-xl mx-auto">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search locations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input pr-24"
              data-testid="map-search-input"
            />
            <button 
              type="submit" 
              className="absolute right-1.5 top-1/2 -translate-y-1/2 bg-brand text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-brand-dark transition-colors"
              data-testid="map-search-button"
            >
              Search
            </button>
          </form>

          {/* Venue Type Filters - Multi-select */}
          <div className="flex gap-2 overflow-x-auto hide-scrollbar pb-1 max-w-xl mx-auto justify-center">
            {venueTypes.map((item) => {
              const isActive = activeFilters.includes(item.type);
              return (
                <button
                  key={item.type}
                  onClick={() => handleTypeFilter(item.type)}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg whitespace-nowrap text-sm font-medium transition-all shadow-sm ${
                    isActive
                      ? "bg-brand text-white shadow-brand/25"
                      : "bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                  data-testid={`filter-${item.type}`}
                >
                  <item.icon className="w-4 h-4" />
                  <span>{item.label}</span>
                </button>
              );
            })}
            {/* Clear filters button - only show when filters are active */}
            {activeFilters.length > 0 && (
              <button
                onClick={() => setActiveFilters([])}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg whitespace-nowrap text-sm font-medium transition-all bg-gray-100 text-gray-600 hover:bg-gray-200"
                data-testid="clear-type-filters"
              >
                <X className="w-4 h-4" />
                <span>Clear</span>
              </button>
            )}
          </div>
          </div>
        )}

        {/* Find Nearest Button - Prominent CTA */}
        <button
          onClick={() => {
            setShowNearbyPanel(true);
            setShowVenueList(false);
            if (!userLocation) {
              getUserLocation(true);
            }
          }}
          disabled={gettingLocation}
          className="absolute bottom-40 md:bottom-36 left-4 z-[1000] bg-brand text-white shadow-lg rounded-full px-4 py-3 hover:bg-brand-dark transition-all flex items-center gap-2 font-medium disabled:opacity-70"
          data-testid="find-nearest-button"
        >
          <Navigation className={`w-5 h-5 ${gettingLocation ? 'animate-pulse' : ''}`} />
          <span className="hidden sm:inline">Find Nearest</span>
        </button>

        {/* Near Me Button - Secondary (centers map on user) */}
        <button
          onClick={() => getUserLocation(false)}
          disabled={gettingLocation}
          className="absolute bottom-40 md:bottom-36 right-4 z-[1000] bg-white shadow-lg rounded-full p-3 hover:bg-gray-50 transition-all border border-gray-200"
          data-testid="near-me-button"
          title="Center map on my location"
        >
          <Locate className={`w-5 h-5 text-brand ${gettingLocation ? 'animate-pulse' : ''}`} />
        </button>

        {/* Show List Button - Mobile */}
        {!showVenueList && !showNearbyPanel && !loading && (
          <button
            onClick={() => setShowVenueList(true)}
            className="md:hidden fixed bottom-32 left-1/2 -translate-x-1/2 z-[1000] bg-brand text-white px-5 py-3 rounded-full shadow-lg flex items-center gap-2 font-medium"
            data-testid="show-list-btn"
          >
            <List className="w-5 h-5" />
            {displayVenues.length} Places
          </button>
        )}

        {/* Venue List Panel */}
        <AnimatePresence>
          {showVenueList && (
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 300 }}
              className="fixed md:absolute bottom-0 left-0 right-0 md:right-auto md:top-0 md:w-96 bg-white shadow-2xl md:shadow-xl z-[1001] rounded-t-2xl md:rounded-none flex flex-col max-h-[70vh] md:max-h-full"
            >
              {/* Panel Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-100">
                <div>
                  <h2 className="font-semibold text-gray-900">
                    {nearbyVenues.length > 0 ? "Nearby" : "All Workspaces"}
                  </h2>
                  <p className="text-sm text-gray-500">
                    {loading ? "Loading..." : `${displayVenues.length} found`}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowVenueList(false);
                    setNearbyVenues([]);
                    setActiveFilter(null);
                  }}
                  className="p-2 hover:bg-gray-100 rounded-full"
                  data-testid="close-panel"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              {/* Venue List */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {loading ? (
                  Array(4).fill(0).map((_, i) => (
                    <div key={`skeleton-${i}`} className="bg-gray-100 rounded-xl h-24 animate-pulse" />
                  ))
                ) : displayVenues.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-500">No venues found</p>
                  </div>
                ) : (
                  displayVenues.map((venue, index) => (
                    <motion.div
                      key={venue.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.03 }}
                      onMouseEnter={() => setSelectedVenue(venue.id)}
                      onMouseLeave={() => setSelectedVenue(null)}
                    >
                      <VenueCard venue={venue} compact={true} />
                    </motion.div>
                  ))
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Nearby Panel - Top 4 Nearest */}
        <AnimatePresence>
          {showNearbyPanel && (
            <NearbyPanel
              venues={nearbyVenues}
              loading={loading}
              userLocation={userLocation}
              gettingLocation={gettingLocation}
              onClose={() => setShowNearbyPanel(false)}
              onLocate={() => getUserLocation(true)}
            />
          )}
        </AnimatePresence>

        {/* Bottom Action Bar + Google Ads - Fixed to bottom */}
        <div className="fixed bottom-0 left-0 right-0 z-[999]">
          {/* Google Ad Banner */}
          <div className="bg-white/95 backdrop-blur-sm py-1.5 px-4 border-t border-gray-200">
            <div className="max-w-4xl mx-auto">
              <AdBanner 
                adSlot="" 
                adFormat="horizontal" 
                className="min-h-[50px]"
              />
            </div>
          </div>
          
          {/* Main Action Bar - Taller with safe area padding for mobile */}
          <div className="bg-gray-900/95 backdrop-blur text-white py-4 px-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
            <div className="flex items-center justify-center gap-4 md:gap-8">
              <span className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-brand-light" />
                <span className="font-medium">{venues.length}+ Locations</span>
              </span>
              <span className="hidden sm:inline text-gray-500">|</span>
              <button 
                onClick={() => navigate('/add-location')}
                className="flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-white px-4 py-2 rounded-lg transition-colors font-medium text-sm shadow-sm"
                data-testid="add-location-link"
              >
                <Plus className="w-4 h-4" />
                <span className="hidden sm:inline">Add Location</span>
                <span className="sm:hidden">Add</span>
              </button>
              <button 
                onClick={() => navigate('/claim')}
                className="flex items-center gap-2 bg-brand hover:bg-brand-dark text-white px-4 py-2 rounded-lg transition-colors font-medium text-sm"
                data-testid="claim-venue-link"
              >
                <span className="hidden sm:inline">Own a venue? Claim it</span>
                <span className="sm:hidden">Claim</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
